import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Book, Download, Edit, Filter, TrendingUp } from "react-feather";

import { useGetEmployeeDetailsQuery } from "AppQuries/employee/get-employee-details";
import { loggedInUser } from "core/redux/slice/authSlice";
import { all_routes } from "Router/all_routes";
import { encrypt } from "core/Operations/cryptHelper";

import Error404 from "MainPages/ErrorPages/error404";
import LoadingSpinner from "InitialPage/Sidebar/LoadingSpinner";

import EmployeeHeader from "./Details/Header";
import EmployeeInformationDetails from "./Details/EmployeeInformation";
import PersonalInformationDetails from "./Details/PersonalInformation";
import SalaryDetails from "./Details/SalaryDetail";
import BankDetails from "./Details/BankDetails";
import PassDetails from "./Details/PassDetail";
import PassportDetails from "./Details/PassportDeatils";
import AllowanceAndDeductionDetails from "./Details/AllowanceAndDeduction";
import WorkingHoursDetails from "./Details/WorkingHoursDetails";
import EducationDetails from "./Details/EductionDetails";
import WorkExperienceDetails from "./Details/WorkExperienceDetails";
import VisaDetails from "./Details/VisaDetails";
import InventoryDetails from "./Details/InventroryDetail";
import CCDetails from "./Details/CCDetails";
import AccomendationDetails from "./Details/AccomendationDetails";
import KidsDetails from "./Details/KidsDetail";
import DependantDetails from "./Details/DependantDetails";
import ImageSection from "./Details/ImageSection";
import "../../style/css/employee-profile.css";
import ContractDetails from "./Details/ContractDetails";
const ViewEmployee = () => {
  const userData = useSelector(loggedInUser);
  const navigate = useNavigate();
  const componentRef = useRef();
  const { code } = useParams();

  const { data, isFetching, isError, error } = useGetEmployeeDetailsQuery({
    companyCode: userData?.companyCode || "",
    locationCode: userData?.selectedLocationCode || "",
    employeeCode: code || "",
  });

  const employeeDetails = data && Array.isArray(data) ? data[0] : null;

  /* ---------------- DEFAULT SECTIONS ---------------- */
  const defaultSections = {
    employeeInfo: true,
    personalInfo: true,
    pass: true,
    passport: true,
    salary: true,
    bank: true,
    allowance: true,
    education: true,
    work: true,
    visa: true,
    inventory: true,
    course: true,
    accommodation: true,
    kids: true,
    dependants: true,
    workingHourDetails: true,
    images: true,
    contract : true,
  };

  const [selectedSections, setSelectedSections] = useState(defaultSections);

  const handleCheckboxChange = (key) => {
    setSelectedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /* ---------------- SECTION GROUPS ---------------- */
  const sectionGroups = [
    {
      title: "Basic Information",
      sections: [
        ["employeeInfo", "Employee Info"],
        ["personalInfo", "Personal Info"],
        ["workingHourDetails", "Working Hours"],
      ],
    },
    {
      title: "Employment & Payroll",
      sections: [
        ["salary", "Salary"],
        ["bank", "Bank"],
        ["allowance", "Allowance"],
        ["education", "Education"],
        ["work", "Work Experience"],
      ],
    },
    {
      title: "Documents & Compliance",
      sections: [
        ["pass", "Pass"],
        ["passport", "Passport"],
        ["visa", "Visa"],
        ["course", "Course"],
        ["inventory", "Inventory"],
        ["contract", "Contract"],
      ],
    },
    {
      title: "Family & Accommodation",
      sections: [
        ["accommodation", "Accommodation"],
        ["kids", "Kids"],
        ["dependants", "Dependants"],
      ],
    },
    {
      title: "Media",
      sections: [["images", "Images"]],
    },
  ];

  /* ---------------- PRINT ---------------- */
  const handleEmployeePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `${employeeDetails?.fullName}-Profile`,
    pageStyle: `
      @media print {
        .page-header, .accordion {
          display: none !important;
        }
        * {
          overflow: visible !important;
        }
      }
    `,
  });

  if (isError) return <Error404 error={error} />;
  if (isFetching) return <LoadingSpinner />;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* ---------------- HEADER ---------------- */}
        <div className="page-header">
          <div className="page-title">
            <h4>Employee Profile</h4>
            <h6>View Employee Details</h6>
          </div>

          <div className="page-btn d-flex">
            <Link
              to={`${all_routes.employee}${all_routes.employee_edit}/${code}`}
              className="btn btn-secondary"
            >
              <Edit className="me-2" /> Edit
            </Link>

            <Link
              to={`${all_routes.employee}${all_routes.employee_hike}?companyCode=${userData?.companyCode}&employeeCode=${code}`}
              className="btn btn-warning ms-2"
            >
              <TrendingUp className="me-2" /> Hike
            </Link>

            <Link
              to={`${all_routes.employee}${
                all_routes.employee_offer_letter
              }?companyCode=${encrypt(
                userData?.companyCode
              )}&employeeCode=${encrypt(code)}`}
              className="btn btn-warning ms-2"
            >
              <Book className="me-2" /> Offer Letters
            </Link>

            <button
              className="btn btn-secondary ms-2"
              onClick={handleEmployeePrint}
            >
              <Download className="me-2" /> Download
            </button>

            <button
              className="btn btn-secondary ms-2"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-reply me-1" /> Back
            </button>
          </div>
        </div>

        {/* ---------------- ACCORDION FILTER ---------------- */}
        <div className="card shadow-sm mb-4">
          <div className="card-body p-0">
            <div className="accordion" id="employeeSectionAccordion">
              <div className="accordion-item border-0">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#allSections"
                  >
                    Employee Profile Sections
                  </button>
                </h2>

                <div
                  id="allSections"
                  className="accordion-collapse collapse"
                  data-bs-parent="#employeeSectionAccordion"
                >
                  <div className="accordion-body">
                    <div className="row">
                      {Object.keys(defaultSections).map((key) => (
                        <div
                          className="col-lg-3 col-md-4 col-sm-6 mb-3"
                          key={key}
                        >
                          <div className="form-check section-checkbox">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`checkbox-${key}`}
                              checked={selectedSections[key]}
                              onChange={() => handleCheckboxChange(key)}
                            />
                            <label
                              className="form-check-label fw-medium"
                              htmlFor={`checkbox-${key}`}
                            >
                              {key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (s) => s.toUpperCase())}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- DETAILS ---------------- */}
        {employeeDetails && (
          <div ref={componentRef}>
            <div className="card border-0">
              <div className="card-body">
                <div className="row">
                  <EmployeeHeader employee={employeeDetails} />

                  {selectedSections.employeeInfo && (
                    <div className="col-md-6">
                      <EmployeeInformationDetails employee={employeeDetails} />
                    </div>
                  )}

                  {selectedSections.personalInfo && (
                    <div className="col-md-6">
                      <PersonalInformationDetails employee={employeeDetails} />
                    </div>
                  )}

                  {selectedSections.salary && (
                    <div className="col-md-6">
                      <SalaryDetails employee={employeeDetails} />
                    </div>
                  )}

                  {selectedSections.bank && (
                    <div className="col-md-6">
                      <BankDetails employee={employeeDetails} />
                    </div>
                  )}

                  {selectedSections.pass && (
                    <PassDetails employee={employeeDetails} />
                  )}
                  {selectedSections.passport && (
                    <PassportDetails employee={employeeDetails} />
                  )}
                  {selectedSections.allowance && (
                    <AllowanceAndDeductionDetails employee={employeeDetails} />
                  )}
                  {selectedSections.workingHourDetails && (
                    <WorkingHoursDetails employee={employeeDetails} />
                  )}
                  {selectedSections.education && (
                    <EducationDetails employee={employeeDetails} />
                  )}
                  {selectedSections.work && (
                    <WorkExperienceDetails employee={employeeDetails} />
                  )}
                  {selectedSections.visa && (
                    <VisaDetails employee={employeeDetails} />
                  )}
                  {selectedSections.contract && (
                    <ContractDetails employee={employeeDetails} />
                  )}                  
                  {selectedSections.inventory && (
                    <InventoryDetails employee={employeeDetails} />
                  )}
                  {selectedSections.course && (
                    <CCDetails employee={employeeDetails} />
                  )}
                  {selectedSections.accommodation && (
                    <AccomendationDetails employee={employeeDetails} />
                  )}
                  {selectedSections.kids && (
                    <KidsDetails employee={employeeDetails} />
                  )}
                  {selectedSections.dependants && (
                    <DependantDetails employee={employeeDetails} />
                  )}
                </div>
              </div>
            </div>

            {selectedSections.images && (
              <div className="card border-0 mt-3">
                <div className="card-header">
                  <h4 className="card-title">Employee Documents</h4>
                </div>
                <div className="card-body">
                  <ImageSection employee={employeeDetails} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewEmployee;
